// Helper functions for portfolio navigation without URL parameters
// Uses sessionStorage to pass the selected portfolio between pages

const PENDING_PORTFOLIO_KEY = 'pending_portfolio_selection';

export interface PendingPortfolio {
  portfolioKey: string;
  timestamp: number;
}

/**
 * Set a pending portfolio to be selected when navigating to the portfolios page
 */
export function setPendingPortfolio(portfolioKey: string): void {
  if (typeof window === 'undefined') return;
  
  const pending: PendingPortfolio = {
    portfolioKey,
    timestamp: Date.now(),
  };
  
  sessionStorage.setItem(PENDING_PORTFOLIO_KEY, JSON.stringify(pending));
}

/**
 * Get and clear the pending portfolio selection
 * Returns null if no pending selection or if it's older than 30 seconds
 */
export function consumePendingPortfolio(): string | null {
  if (typeof window === 'undefined') return null;
  
  try {
    const raw = sessionStorage.getItem(PENDING_PORTFOLIO_KEY);
    if (!raw) return null;
    
    // Clear immediately to prevent double consumption
    sessionStorage.removeItem(PENDING_PORTFOLIO_KEY);
    
    const pending: PendingPortfolio = JSON.parse(raw);
    
    // Expire after 30 seconds to prevent stale selections
    if (Date.now() - pending.timestamp > 30000) {
      return null;
    }
    
    return pending.portfolioKey;
  } catch {
    return null;
  }
}

/**
 * Check if there is a valid pending portfolio without consuming it.
 * Used to skip localStorage restoration when a pending selection exists.
 */
export function hasPendingPortfolio(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const raw = sessionStorage.getItem(PENDING_PORTFOLIO_KEY);
    if (!raw) return false;
    const pending: PendingPortfolio = JSON.parse(raw);
    return Date.now() - pending.timestamp <= 30000;
  } catch {
    return false;
  }
}

/**
 * Navigate to portfolios page with a specific portfolio selected
 * For same-tab navigation
 */
export function navigateToPortfolio(portfolioKey: string, router: { push: (url: string) => void }): void {
  setPendingPortfolio(portfolioKey);
  router.push('/portfolios');
}

/**
 * Open portfolios page with a specific portfolio in a new tab.
 * Uses sessionStorage which is cloned into the new tab on open.
 */
export function openPortfolioInNewTab(portfolioKey: string): void {
  setPendingPortfolio(portfolioKey);
  window.open('/portfolios', '_blank');
}
